import { useEffect, useState, useRef } from 'react';
import { format, startOfWeek, addDays, isToday } from 'date-fns';
import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight, Download, Upload, Plus, Edit2, Trash2, X } from 'lucide-react';
import { useShiftStore, useAuthStore, useUserStore, useToastStore } from '../store';
import { Spinner, Modal, Badge, UserSearchSelect } from '../components/ui';
import { api } from '../utils/api';
import type { Shift } from '../types';

export function SchedulePage() {
  const { shifts, isLoading, fetchShifts } = useShiftStore();
  const { user } = useAuthStore();
  const { users, fetchUsers } = useUserStore();
  const { addToast } = useToastStore();
  
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [modalMode, setModalMode] = useState<'request' | 'assign' | 'reassign' | 'edit' | 'create' | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [newStartTime, setNewStartTime] = useState('09:00');
  const [newEndTime, setNewEndTime] = useState('17:00');
  const [newShiftName, setNewShiftName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const isSuperAdmin = user?.role === 'SuperAdmin';

  useEffect(() => {
    fetchShifts();
    if (isAdmin) {
      fetchUsers();
    }
  }, []);

  // Refresh shifts when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchShifts();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchShifts]);

  const getWeekDates = (offset: number) => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 0 });
    const weekStart = addDays(start, offset * 7);
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const currentWeekDates = getWeekDates(weekOffset);
  const nextWeekDates = getWeekDates(weekOffset + 1);

  const getShiftsForDate = (date: Date): Shift[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    if (!shifts || !Array.isArray(shifts)) {
      return [];
    }
    return shifts
      .filter(s => s.date === dateStr && !s.deleted) // Filter out deleted shifts
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleShiftClick = (shift: Shift, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    if (shift.status === 'Available') {
      setSelectedShift(shift);
      setModalMode(isAdmin ? 'assign' : 'request');
      setSelectedUserId('');
    } else if (shift.status === 'Assigned' && isAdmin) {
      setSelectedShift(shift);
      setModalMode('reassign');
      setSelectedUserId('');
    }
  };

  const handleEditShift = (shift: Shift, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isAdmin) return;
    
    setSelectedShift(shift);
    setNewStartTime(shift.start_time);
    setNewEndTime(shift.end_time);
    setNewShiftName(shift.name || '');
    setModalMode('edit');
  };

  const handleCreateShift = (date: Date) => {
    if (!isAdmin) return;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    setEditingDate(dateStr);
    setNewStartTime('09:00');
    setNewEndTime('17:00');
    setNewShiftName('');
    setModalMode('create');
  };

  const handleSaveEdit = async () => {
    if (!selectedShift) return;

    setIsSubmitting(true);
    const result = await api.updateShift(selectedShift.id, newStartTime, newEndTime, newShiftName || null);
    setIsSubmitting(false);

    if (result.success) {
      addToast('Shift updated successfully!', 'success');
      fetchShifts();
      closeModal();
    } else {
      addToast(result.error || 'Failed to update shift', 'error');
    }
  };

  const handleSaveCreate = async () => {
    if (!editingDate) return;

    setIsSubmitting(true);
    const result = await api.createShift(editingDate, newStartTime, newEndTime, newShiftName || null);
    setIsSubmitting(false);

    if (result.success) {
      addToast('Shift created successfully!', 'success');
      fetchShifts();
      closeModal();
    } else {
      addToast(result.error || 'Failed to create shift', 'error');
    }
  };

  const handleDeleteShift = async (shift: Shift, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    if (!isAdmin) return;
    
    if (!confirm(`Are you sure you want to delete this shift?\n\nDate: ${format(new Date(shift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}\nTime: ${formatTime(shift.start_time)} - ${formatTime(shift.end_time)}\n\nThis will prevent it from being regenerated by permanent shifts.`)) {
      return;
    }

    setIsSubmitting(true);
    const result = await api.deleteShift(shift.id);
    setIsSubmitting(false);

    if (result.success) {
      addToast('Shift deleted successfully!', 'success');
      fetchShifts();
    } else {
      addToast(result.error || 'Failed to delete shift', 'error');
    }
  };

  const handleRequestShift = async () => {
    if (!selectedShift) return;
    
    setIsSubmitting(true);
    const result = await api.createRequest(selectedShift.id);
    setIsSubmitting(false);

    if (result.success) {
      addToast('Shift request submitted!', 'success');
      fetchShifts();
    } else {
      addToast(result.error || 'Failed to request shift', 'error');
    }
    
    closeModal();
  };

  const handleAssignShift = async () => {
    if (!selectedShift || !selectedUserId) {
      addToast('Please select a user', 'warning');
      return;
    }

    setIsSubmitting(true);
    const result = await api.assignShift(selectedShift.id, selectedUserId);
    setIsSubmitting(false);

    if (result.success) {
      addToast('Shift assigned successfully!', 'success');
      fetchShifts();
    } else {
      addToast(result.error || 'Failed to assign shift', 'error');
    }
    
    closeModal();
  };

  const handleUnassignShift = async () => {
    if (!selectedShift) return;

    setIsSubmitting(true);
    const result = await api.unassignShift(selectedShift.id);
    setIsSubmitting(false);

    if (result.success) {
      addToast('Shift unassigned successfully!', 'success');
      fetchShifts();
    } else {
      addToast(result.error || 'Failed to unassign shift', 'error');
    }
    
    closeModal();
  };

  const handleUnassignUser = async (userId: string) => {
    if (!selectedShift) return;

    setIsSubmitting(true);
    const result = await api.unassignShift(selectedShift.id, userId);
    setIsSubmitting(false);

    if (result.success) {
      addToast('User unassigned successfully!', 'success');
      // Refresh shifts and update selected shift
      await fetchShifts();
      // Update selected shift to reflect the change
      if (result.data) {
        setSelectedShift(result.data);
      }
    } else {
      addToast(result.error || 'Failed to unassign user', 'error');
    }
  };

  const closeModal = () => {
    setSelectedShift(null);
    setModalMode(null);
    setSelectedUserId('');
    setEditingDate(null);
    setNewShiftName('');
  };

  const handleExport = async () => {
    if (!isSuperAdmin || isExporting) return;
    
    setIsExporting(true);
    try {
      const result = await api.exportData();
      if (result.success) {
        addToast('Data exported successfully!', 'success');
      } else {
        addToast(result.error || 'Failed to export data', 'error');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    if (!isSuperAdmin || isImporting) return;
    fileInputRef.current?.click();
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSuperAdmin || !event.target.files?.[0]) return;

    const file = event.target.files[0];
    setIsImporting(true);

    try {
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      if (!importData.data) {
        addToast('Invalid import file format', 'error');
        setIsImporting(false);
        return;
      }

      const tenantSlugs = (importData.data.tenants || [])
        .map((t: { slug?: string }) => t.slug)
        .filter(Boolean)
        .join(', ');
      const confirmMessage = tenantSlugs
        ? `This will replace all data for tenant(s): ${tenantSlugs}. Users are matched by username and shifts by date/time within each tenant slug. Continue?`
        : 'This will replace data for all tenant slugs found in the backup file. Continue?';
      if (!window.confirm(confirmMessage)) {
        setIsImporting(false);
        return;
      }

      const result = await api.importData(importData.data, importData.version);

      if (result.success) {
        const summary = (result as {
          summary?: {
            users?: number;
            usersCreated?: number;
            usersUpdated?: number;
            usersUnchanged?: number;
            shifts?: number;
          };
        }).summary;
        const userParts = [
          summary?.usersCreated ? `${summary.usersCreated} created` : null,
          summary?.usersUpdated ? `${summary.usersUpdated} updated` : null,
          summary?.usersUnchanged ? `${summary.usersUnchanged} unchanged` : null,
        ].filter(Boolean);
        const userSummary = userParts.length > 0
          ? userParts.join(', ')
          : `${summary?.users || 0} users`;
        addToast(
          `Data imported successfully! (${userSummary}, ${summary?.shifts || 0} shifts)`,
          'success'
        );
        fetchShifts();
        if (isAdmin) {
          fetchUsers();
        }
      } else {
        addToast(result.error || 'Failed to import data', 'error');
      }
    } catch (error) {
      addToast('Failed to read import file', 'error');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const renderWeekColumn = (dates: Date[], title: string) => (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-7 gap-4">
            {dates.map((date, i) => {
              const dayShifts = getShiftsForDate(date);
              
              return (
                <div key={i} className="flex flex-col">
                  {/* Day Header */}
                  <div className={clsx(
                    'p-3 text-center text-sm font-medium rounded-t-xl mb-2',
                    isToday(date)
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-700 text-white'
                  )}>
                    <div>{format(date, 'EEE')}</div>
                    <div className="text-xs opacity-80">{format(date, 'MMM d')}</div>
                    {isToday(date) && (
                      <div className="text-[10px] mt-1 bg-white/20 rounded px-1.5 py-0.5 inline-block">
                        TODAY
                      </div>
                    )}
                  </div>

                  {/* Shifts for this day */}
                  <div className="flex-1 min-h-[200px] bg-slate-50 rounded-b-xl p-2 space-y-2">
                    {dayShifts.length === 0 ? (
                      <div className="text-center text-xs text-slate-400 py-4">
                        No shifts
                      </div>
                    ) : (
                      dayShifts.map(shift => (
                        <ShiftCard
                          key={shift.id}
                          shift={shift}
                          isAdmin={isAdmin}
                          onClick={(e) => handleShiftClick(shift, e)}
                          onEdit={(e) => handleEditShift(shift, e)}
                          onDelete={(e) => handleDeleteShift(shift, e)}
                        />
                      ))
                    )}
                    
                    {/* Add shift button (admin only) */}
                    {isAdmin && (
                      <button
                        onClick={() => handleCreateShift(date)}
                        className="w-full p-2 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 transition-colors flex items-center justify-center gap-1 text-xs"
                        title="Add custom shift"
                      >
                        <Plus className="w-3 h-3" />
                        Add Shift
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading && (!shifts || !Array.isArray(shifts) || shifts.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Schedule</h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Click on shifts to assign or edit times' : 'Click on available shifts to request'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="btn btn-secondary btn-sm"
                title="Export all data"
              >
                {isExporting ? (
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline ml-1">Export</span>
              </button>
              <button
                onClick={handleImportClick}
                disabled={isImporting}
                className="btn btn-secondary btn-sm"
                title="Import data"
              >
                {isImporting ? (
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span className="hidden sm:inline ml-1">Import</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </>
          )}
          <button
            onClick={() => setWeekOffset(w => w - 1)}
            className="btn btn-secondary btn-sm"
            disabled={false}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="btn btn-secondary btn-sm"
            disabled={weekOffset === 0}
          >
            Today
          </button>
          <button
            onClick={() => setWeekOffset(w => w + 1)}
            className="btn btn-secondary btn-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-xl border border-slate-200">
        <span className="text-sm font-medium text-slate-700">Legend:</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-300" />
          <span className="text-sm text-slate-600">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-100 border-2 border-amber-300" />
          <span className="text-sm text-slate-600">Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-100 border-2 border-emerald-300" />
          <span className="text-sm text-slate-600">Assigned</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-300" />
          <span className="text-sm text-slate-600">Permanent</span>
        </div>
      </div>

      {/* Calendar grids */}
      {renderWeekColumn(currentWeekDates, weekOffset === 0 ? 'This Week' : `Week of ${format(currentWeekDates[0], 'MMM d')}`)}
      {renderWeekColumn(nextWeekDates, weekOffset === 0 ? 'Next Week' : `Week of ${format(nextWeekDates[0], 'MMM d')}`)}

      {/* Request Modal */}
      <Modal
        isOpen={modalMode === 'request'}
        onClose={closeModal}
        title="Request Shift"
      >
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(selectedShift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-slate-600">
                {formatTime(selectedShift.start_time)} - {formatTime(selectedShift.end_time)}
              </p>
            </div>
            <p className="text-slate-600">
              Would you like to request this shift? An admin will review your request.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={closeModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleRequestShift}
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Request Shift'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Modal */}
      <Modal
        isOpen={modalMode === 'assign'}
        onClose={closeModal}
        title="Assign Shift"
      >
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(selectedShift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-slate-600">
                {formatTime(selectedShift.start_time)} - {formatTime(selectedShift.end_time)}
              </p>
            </div>
            <div>
              <label className="input-label">Assign to User</label>
              <UserSearchSelect
                users={users}
                value={selectedUserId}
                onChange={setSelectedUserId}
                placeholder="Type to search users..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={closeModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleAssignShift}
                disabled={isSubmitting || !selectedUserId}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Assign Shift'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reassign Modal */}
      <Modal
        isOpen={modalMode === 'reassign'}
        onClose={closeModal}
        title="Manage Shift Assignment"
      >
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(selectedShift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-slate-600">
                {formatTime(selectedShift.start_time)} - {formatTime(selectedShift.end_time)}
              </p>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-2">Currently assigned to:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedShift.assigned_users.map(u => (
                    <Badge key={u.id} variant="info" className="flex items-center gap-1.5 pr-1">
                      <span>{u.display_name || u.username}</span>
                      <button
                        onClick={() => handleUnassignUser(u.id)}
                        disabled={isSubmitting}
                        className="ml-1 hover:bg-slate-200 rounded-full p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Unassign user"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Type: {selectedShift.assignment_type}
                </p>
              </div>
            </div>
            
            <div>
              <label className="input-label">Reassign to User (optional)</label>
              <UserSearchSelect
                users={users}
                value={selectedUserId}
                onChange={setSelectedUserId}
                placeholder="Type to search users..."
                excludeUserIds={selectedShift.assigned_users.map((u) => u.id)}
              />
            </div>

            <div className="flex gap-3 justify-between">
              <button
                onClick={handleUnassignShift}
                disabled={isSubmitting}
                className="btn btn-danger"
              >
                Unassign All
              </button>
              <div className="flex gap-3">
                <button onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleAssignShift}
                  disabled={isSubmitting || !selectedUserId}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Add User'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Shift Times Modal */}
      <Modal
        isOpen={modalMode === 'edit'}
        onClose={closeModal}
        title="Edit Shift Times"
      >
        {selectedShift && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(selectedShift.date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <div>
              <label className="input-label">Start Time</label>
              <input
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="input-label">End Time</label>
              <input
                type="time"
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="input-label">Shift Name (optional)</label>
              <input
                type="text"
                value={newShiftName}
                onChange={(e) => setNewShiftName(e.target.value)}
                className="input"
                placeholder="e.g., Morning Shift, Night Shift"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={closeModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Shift Modal */}
      <Modal
        isOpen={modalMode === 'create'}
        onClose={closeModal}
        title="Create Custom Shift"
      >
        {editingDate && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">
                {format(new Date(editingDate + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <div>
              <label className="input-label">Start Time</label>
              <input
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="input-label">End Time</label>
              <input
                type="time"
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="input-label">Shift Name (optional)</label>
              <input
                type="text"
                value={newShiftName}
                onChange={(e) => setNewShiftName(e.target.value)}
                className="input"
                placeholder="e.g., Morning Shift, Night Shift"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={closeModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSaveCreate}
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Create Shift'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Shift Card Component
interface ShiftCardProps {
  shift: Shift;
  isAdmin: boolean;
  onClick: (e: React.MouseEvent) => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}

function ShiftCard({ shift, isAdmin, onClick, onEdit, onDelete }: ShiftCardProps) {
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getCardStyles = () => {
    switch (shift.status) {
      case 'Available':
        return 'bg-red-50 border-red-300 hover:bg-red-100';
      case 'Pending':
        return 'bg-amber-50 border-amber-300 hover:bg-amber-100';
      case 'Assigned':
        return shift.assignment_type === 'Permanent' 
          ? 'bg-blue-50 border-blue-300 hover:bg-blue-100' 
          : 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100';
      default:
        return 'bg-slate-50 border-slate-300';
    }
  };

  const isClickable = shift.status === 'Available' || (shift.status === 'Assigned' && isAdmin);

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={clsx(
        'p-3 rounded-lg border-2 transition-colors relative',
        getCardStyles(),
        isClickable && 'cursor-pointer'
      )}
    >
      {/* Edit and Delete buttons (admin only) */}
      {isAdmin && (
        <div className="absolute top-1 right-1 flex gap-1">
          <button
            onClick={onEdit}
            className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
            title="Edit shift times"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          {shift.status === 'Available' && (
            <button
              onClick={onDelete}
              className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
              title="Delete shift"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
      
      {/* Shift name (if provided) */}
      {shift.name && (
        <div className="text-xs font-bold text-slate-900 mb-1">
          {shift.name}
        </div>
      )}
      
      {/* Time display */}
      <div className="text-xs font-semibold text-slate-900 mb-1">
        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
      </div>
      
      {/* Status badge */}
      <span className="text-[10px]">
        <Badge
          variant={
            shift.status === 'Available' ? 'danger' :
            shift.status === 'Pending' ? 'warning' :
            shift.assignment_type === 'Permanent' ? 'info' : 'success'
          }
        >
          {shift.status === 'Assigned' && shift.assignment_type === 'Permanent' 
            ? 'Permanent' 
            : shift.status}
        </Badge>
      </span>
      
      {/* Assigned users */}
      {shift.assigned_users.length > 0 && (
        <div className="mt-2 text-[11px] font-medium text-slate-700 leading-tight">
          {shift.assigned_users.map(u => u.display_name || u.username).join(', ')}
        </div>
      )}
    </div>
  );
}
